import React, { useState, useEffect } from 'react';
import { Search, Settings2, Map, Route, Eye, Volume2, Layers, RefreshCw } from 'lucide-react';
import axios from 'axios';

interface UserPref {
    id: number;
    user_id: number;
    user_username: string;
    user_name?: string;
    user_avatar?: string;
    map_style: string;
    show_routes: boolean;
    show_3d: boolean;
    show_learned_routes: boolean;
    route_deviation_alerts: boolean;
    allow_sound_requests: boolean;
}

interface PrefStats {
    total_users: number;
    show_3d_enabled: number;
    show_routes_enabled: number;
    satellite_map_users: number;
}

const UserPrefManagement: React.FC = () => {
    const [prefs, setPrefs] = useState<UserPref[]>([]);
    const [stats, setStats] = useState<PrefStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setRefreshing(true);
        try {
            const [prefsRes, statsRes] = await Promise.all([
                axios.get('https://api.lifeshare.app/api/v1/admin/user-preferences'),
                axios.get('https://api.lifeshare.app/api/v1/admin/user-preferences/stats')
            ]);
            setPrefs(prefsRes.data.preferences || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const BoolBadge = ({ value }: { value: boolean }) => (
        <span className={`w-3 h-3 rounded-full ${value ? 'bg-green-500' : 'bg-gray-300'}`} />
    );

    const getMapStyleBadge = (style: string) => {
        switch (style) {
            case 'satellite': return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-xl text-xs">🛰️ Uydu</span>;
            case 'hybrid': return <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-xl text-xs">🗺️ Hibrit</span>;
            default: return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-xl text-xs">📍 Standart</span>;
        }
    };

    const filteredPrefs = prefs.filter(p =>
        (p.user_username || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (p.user_name || '').toLowerCase().includes(searchText.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="max-w-5xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Harita Tercihleri</h1>
                        <p className="text-sm text-gray-500 mt-1">Kullanıcı harita ve rota ayarlarını görüntüle</p>
                    </div>
                    <button onClick={fetchData} disabled={refreshing} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
                        <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Settings2 className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-purple-700">{stats.total_users}</p>
                            <p className="text-xs text-purple-600">Toplam</p>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Layers className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-blue-700">{stats.show_3d_enabled}</p>
                            <p className="text-xs text-blue-600">3D Açık</p>
                        </div>
                        <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 border border-cyan-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Route className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-cyan-700">{stats.show_routes_enabled}</p>
                            <p className="text-xs text-cyan-600">Rota Açık</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Map className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-green-700">{stats.satellite_map_users}</p>
                            <p className="text-xs text-green-600">Uydu Görünüm</p>
                        </div>
                    </div>
                )}

                {/* Search */}
                <div className="bg-white rounded-2xl p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="text" placeholder="Kullanıcı ara..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500" />
                    </div>
                </div>

                {/* List */}
                {filteredPrefs.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center">
                        <Settings2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">Kullanıcı tercihi bulunamadı</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {filteredPrefs.map((pref) => (
                            <div key={pref.id} className="bg-white rounded-2xl p-4 hover:ring-2 hover:ring-purple-100 transition-all">
                                <div className="flex items-start gap-3 mb-3">
                                    {pref.user_avatar ? (
                                        <img src={`https://api.lifeshare.app${pref.user_avatar}`} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                                    ) : (
                                        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600 flex-shrink-0">
                                            <Map className="w-5 h-5" />
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="font-semibold text-gray-900 text-sm truncate">{pref.user_name || pref.user_username}</p>
                                        <p className="text-xs text-gray-500">@{pref.user_username}</p>
                                    </div>
                                    {getMapStyleBadge(pref.map_style)}
                                </div>

                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-xl"><BoolBadge value={pref.show_routes} /><Route className="w-3 h-3 text-gray-400" /><span>Rotalar</span></div>
                                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-xl"><BoolBadge value={pref.show_3d} /><Layers className="w-3 h-3 text-gray-400" /><span>3D Görünüm</span></div>
                                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-xl"><BoolBadge value={pref.show_learned_routes} /><Eye className="w-3 h-3 text-gray-400" /><span>Öğrenilen Rotalar</span></div>
                                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-xl"><BoolBadge value={pref.route_deviation_alerts} /><span>Rota Sapması</span></div>
                                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-xl col-span-2"><BoolBadge value={pref.allow_sound_requests} /><Volume2 className="w-3 h-3 text-gray-400" /><span>Ses İsteklerine İzin Ver</span></div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserPrefManagement;
